import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Shield,
    Search,
    Edit,
    Trash2,
    CheckCircle,
    XCircle,
    Building2,
    Users,
    Mail,
    Phone,
    RefreshCw,
    Grid3x3,
    List as ListIcon,
    Table as TableIcon,
    Truck,
    Package,
    Plus,
    Key,
    Power,
    ArrowUpDown,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import api from '@/services/api';
import { useAlert } from '@/components/AlertProvider';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/utils/cn';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import SuperAdminAdmins from '../SuperAdmin/Admins';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ClientCreateModal from '@/components/clients/ClientCreateModal';
import { useAuthStore } from '@/stores/authStore';
import { ModuleHeader } from '@/components/layout/ModuleHeader';
import { AddButton } from '@/components/ui/AddButton';

interface AdminUser {
    id: number;
    email: string;
    user_type: 'client' | 'prospect' | 'admin' | 'apporteur' | 'fournisseur' | 'transporteur';
    status?: 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    phone?: string;
    postal_code?: string;
    city?: string;
    is_active: boolean;
    is_email_verified: boolean;
    role?: string;
    created_at: string;
    client_type?: string;
    orders_count?: number;
    total_ca?: number;
    last_login?: string;
}

type AdminTab = 'admin' | 'apporteur' | 'client' | 'fournisseur' | 'transporteur';

const Administration: React.FC = () => {
    const { user } = useAuthStore();
    const isSuperAdmin = user?.role === 'super_admin';
    const { showSuccess, showError } = useAlert();
    const { showConfirm } = useConfirm();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<AdminTab>(isSuperAdmin ? 'admin' : 'apporteur');

    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState<number>(1);
    const [pageSize] = useState<number>(200);
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [statusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>(window.innerWidth < 640 ? 'grid' : 'table');
    const [columnFilters, setColumnFilters] = useState({
        name: '',
        email: '',
        phone: '',
        postal_code: '',
        city: '',
        email_verified: '',
        status: '',
        is_active: '',
        client_type: '',
    });
    const [editModal, setEditModal] = useState<AdminUser | null>(null);
    const [editForm, setEditForm] = useState<Partial<AdminUser>>({});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [tabCounts, setTabCounts] = useState({
        admin: 0,
        apporteur: 0,
        client: 0,
        fournisseur: 0,
        transporteur: 0,
    });

    const onClientCreated = (_newClient: any) => {
        setShowCreateModal(false);
        loadTabCounts();
        // Don't reload - the new client will appear on next manual refresh
    };

    const loadTabCounts = async () => {
        try {
            const response = await api.get('/users/administration/all-users');
            const data = Array.isArray(response.data) ? response.data : [];
            const counts = {
                admin: 0,
                apporteur: 0,
                client: 0,
                fournisseur: 0,
                transporteur: 0,
            };

            data.forEach((user: AdminUser) => {
                switch (user.user_type) {
                    case 'admin':
                        counts.admin += 1;
                        break;
                    case 'apporteur':
                        counts.apporteur += 1;
                        break;
                    case 'client':
                    case 'prospect':
                        counts.client += 1;
                        break;
                    default:
                        break;
                }
            });

            setTabCounts(counts);
        } catch (error) {
            console.error('Erreur lors du chargement des compteurs', error);
        }
    };

    const loadUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/users/administration/all-users', {
                params: {
                    search: search || undefined,
                },
            });
            const filtered = response.data.filter((u: any) => u.user_type === 'client' || u.user_type === 'prospect');
            setUsers(filtered);
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors du chargement des utilisateurs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'client') {
            setPage(1);
            loadUsers();
        }
    }, [activeTab]);

    useEffect(() => {
        loadTabCounts();
    }, [activeTab]);

    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
            if (activeTab === 'client') {
                loadUsers();
            }
        }, 300);
        return () => clearTimeout(t);
    }, [search]);

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const filteredUsers = useMemo(() => {
        if (activeTab !== 'client') return [];

        let filtered = users;
        if (search) {
            const term = search.toLowerCase();
            filtered = filtered.filter(
                (u) =>
                    u.email.toLowerCase().includes(term) ||
                    u.company_name?.toLowerCase().includes(term) ||
                    u.first_name?.toLowerCase().includes(term) ||
                    u.last_name?.toLowerCase().includes(term) ||
                    u.phone?.includes(term) ||
                    u.postal_code?.includes(term) ||
                    u.city?.toLowerCase().includes(term)
            );
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter((u) => statusFilter === 'active' ? u.is_active : !u.is_active);
        }

        // Apply column filters
        if (columnFilters.name) {
            const term = columnFilters.name.toLowerCase();
            filtered = filtered.filter((u) =>
                u.company_name?.toLowerCase().includes(term) ||
                `${u.first_name} ${u.last_name}`.toLowerCase().includes(term)
            );
        }
        if (columnFilters.phone) {
            const term = columnFilters.phone.toLowerCase();
            filtered = filtered.filter((u) => u.phone?.toLowerCase().includes(term));
        }
        if (columnFilters.email) {
            const term = columnFilters.email.toLowerCase();
            filtered = filtered.filter((u) => u.email.toLowerCase().includes(term));
        }
        if (columnFilters.status) {
            const term = columnFilters.status.toLowerCase();
            filtered = filtered.filter((u) => u.status?.toLowerCase() === term);
        }
        if (columnFilters.email_verified) {
            const isVerified = columnFilters.email_verified === 'verifie';
            filtered = filtered.filter((u) => u.is_email_verified === isVerified);
        }
        if (columnFilters.is_active) {
            const isActive = columnFilters.is_active === 'actif';
            filtered = filtered.filter((u) => u.is_active === isActive);
        }
        if (columnFilters.postal_code) {
            const term = columnFilters.postal_code.toLowerCase();
            filtered = filtered.filter((u) => u.postal_code?.toLowerCase().includes(term));
        }
        if (columnFilters.city) {
            const term = columnFilters.city.toLowerCase();
            filtered = filtered.filter((u) => u.city?.toLowerCase().includes(term));
        }
        if (columnFilters.client_type) {
            const term = columnFilters.client_type.toLowerCase();
            filtered = filtered.filter((u) => u.client_type?.toLowerCase().includes(term));
        }

        return filtered;
    }, [search, statusFilter, users, activeTab, columnFilters]);

    const sortedAndPaginatedUsers = useMemo(() => {
        let sorted = [...filteredUsers];

        if (sortColumn) {
            sorted.sort((a, b) => {
                let aVal: any;
                let bVal: any;

                switch (sortColumn) {
                    case 'name':
                        aVal = (a.company_name || `${a.first_name} ${a.last_name}`).toLowerCase();
                        bVal = (b.company_name || `${b.first_name} ${b.last_name}`).toLowerCase();
                        break;
                    case 'email':
                        aVal = a.email.toLowerCase();
                        bVal = b.email.toLowerCase();
                        break;
                    case 'phone':
                        aVal = a.phone || '';
                        bVal = b.phone || '';
                        break;
                    case 'postal_code':
                        aVal = a.postal_code || '';
                        bVal = b.postal_code || '';
                        break;
                    case 'city':
                        aVal = (a.city || '').toLowerCase();
                        bVal = (b.city || '').toLowerCase();
                        break;
                    case 'status':
                        aVal = a.status || '';
                        bVal = b.status || '';
                        break;
                    case 'client_type':
                        aVal = a.client_type || '';
                        bVal = b.client_type || '';
                        break;
                    case 'orders':
                        aVal = a.orders_count || 0;
                        bVal = b.orders_count || 0;
                        break;
                    case 'ca':
                        aVal = a.total_ca || 0;
                        bVal = b.total_ca || 0;
                        break;
                    case 'created_at':
                        aVal = new Date(a.created_at).getTime();
                        bVal = new Date(b.created_at).getTime();
                        break;
                    case 'last_login':
                        aVal = a.last_login ? new Date(a.last_login).getTime() : 0;
                        bVal = b.last_login ? new Date(b.last_login).getTime() : 0;
                        break;
                    default:
                        return 0;
                }

                if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
        }

        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return sorted.slice(startIndex, endIndex);
    }, [filteredUsers, sortColumn, sortDirection, page, pageSize]);

    const handleEdit = (user: AdminUser) => {
        setEditModal(user);
        setEditForm({
            company_name: user.company_name,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone: user.phone,
            client_type: user.client_type,
            status: user.status,
            is_active: user.is_active,
        });
    };

    const handleSaveEdit = async () => {
        if (!editModal) return;
        try {
            await api.patch(`/users/administration/users/${editModal.user_type}/${editModal.id}`, editForm);
            showSuccess('Utilisateur mis à jour avec succès');
            setEditModal(null);
            loadUsers();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const handleToggleActive = async (user: AdminUser) => {
        const clientName = user.company_name || user.email;
        const newStatus = !user.is_active;
        const confirmed = await showConfirm({
            title: newStatus ? 'Activer le compte' : 'Désactiver le compte',
            message: `Êtes-vous sûr de vouloir ${newStatus ? 'activer' : 'désactiver'} le compte de "${clientName}" ?`,
            confirmText: newStatus ? 'ACTIVER' : 'DÉSACTIVER',
            cancelText: 'ANNULER',
            severity: 'warning'
        });

        if (!confirmed) return;

        try {
            await api.patch(`/users/administration/users/${user.user_type}/${user.id}`, { is_active: newStatus });
            showSuccess(`Compte ${newStatus ? 'activé' : 'désactivé'} avec succès`);
            loadUsers();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };

    const handleResetPassword = async (user: AdminUser) => {
        const clientName = user.company_name || user.email;
        const confirmed = await showConfirm({
            title: 'Réinitialiser le mot de passe',
            message: `Êtes-vous sûr de vouloir réinitialiser le mot de passe de "${clientName}" ? Un email de réinitialisation sera envoyé.`,
            confirmText: 'RÉINITIALISER',
            cancelText: 'ANNULER',
            severity: 'warning'
        });

        if (!confirmed) return;

        try {
            await api.post(`/auth/forgot-password`, { email: user.email });
            showSuccess('Email de réinitialisation envoyé avec succès');
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
        }
    };

    const handleDelete = async (user: AdminUser) => {
        const clientName = user.company_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
        const confirmed = await showConfirm({
            title: 'Supprimer le client',
            message: `Êtes-vous sûr de vouloir supprimer l'client "${clientName}" ? Cette action est irréversible.`,
            confirmText: 'SUPPRIMER',
            cancelText: 'ANNULER',
            severity: 'error'
        });

        if (!confirmed) return;

        try {
            await api.delete(`/users/administration/users/${user.user_type}/${user.id}`);
            showSuccess('Client supprimé avec succès');
            loadUsers();
            loadTabCounts();
        } catch (error: any) {
            showError(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'prospect': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
            case 'actif': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'inactif': return 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400';
            case 'bloque': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
            case 'ferme': return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300';
            default: return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
        }
    };

    const getStatusLabel = (status?: string) => {
        switch (status) {
            case 'prospect': return 'Prospect';
            case 'actif': return 'Actif';
            case 'inactif': return 'Inactif';
            case 'bloque': return 'Bloqué';
            case 'ferme': return 'Fermé';
            default: return status || 'N/A';
        }
    };

    const getBuyerTypeLabel = (type?: string): string => {
        if (!type) return 'N/A';
        switch (type) {
            case 'pharmacie': return 'Pharmacie';
            case 'parapharmacie': return 'Parapharmacie';
            case 'centrale':
            case 'central': return 'Centrale d\'Achat';
            case 'magasin_bio': return 'Magasin Bio';
            case 'therapeute': return 'Thérapeute';
            case 'member': return 'Client Membre';
            case 'independent': return 'Indépendant';
            default: return type;
        }
    };

    const getBuyerTypeStyle = (type?: string): string => {
        if (!type) return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
        switch (type) {
            case 'pharmacie': return 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300';
            case 'parapharmacie': return 'bg-teal-100 text-teal-700 dark:bg-teal-950/30 dark:text-teal-300';
            case 'centrale':
            case 'central': return 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300';
            case 'magasin_bio': return 'bg-lime-100 text-lime-700 dark:bg-lime-950/30 dark:text-lime-300';
            case 'therapeute': return 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300';
            case 'member': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-300';
            case 'independent': return 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300';
            default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
        }
    };


    const renderTabs = () => {
        const tabs = [
            isSuperAdmin
                ? {
                    key: 'admin' as AdminTab,
                    label: 'Admin',
                    icon: Shield,
                    count: tabCounts.admin,
                    countClass: 'text-purple-600 dark:text-purple-400',
                    activeClass: 'border-purple-400 shadow-md bg-white/90 dark:bg-slate-900/90',
                    inactiveClass: 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-purple-200/80',
                    gradientClass: 'from-purple-50/70 to-fuchsia-50/70 dark:from-purple-950/20 dark:to-fuchsia-950/20',
                    iconClass: 'bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-300'
                }
                : null,
            {
                key: 'apporteur' as AdminTab,
                label: 'Apporteur',
                icon: Users,
                count: tabCounts.apporteur,
                countClass: 'text-blue-600 dark:text-blue-400',
                activeClass: 'border-blue-400 shadow-md bg-white/90 dark:bg-slate-900/90',
                inactiveClass: 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-blue-200/80',
                gradientClass: 'from-blue-50/70 to-indigo-50/70 dark:from-blue-950/20 dark:to-indigo-950/20',
                iconClass: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300'
            },
            {
                key: 'client' as AdminTab,
                label: 'Clients',
                icon: Building2,
                count: tabCounts.client,
                countClass: 'text-cyan-600 dark:text-cyan-400',
                activeClass: 'border-cyan-400 shadow-md bg-white/90 dark:bg-slate-900/90',
                inactiveClass: 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-cyan-200/80',
                gradientClass: 'from-cyan-50/70 to-sky-50/70 dark:from-cyan-950/20 dark:to-sky-950/20',
                iconClass: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-950/40 dark:text-cyan-300'
            },
            {
                key: 'fournisseur' as AdminTab,
                label: 'Fournisseur',
                icon: Package,
                count: tabCounts.fournisseur,
                countClass: 'text-emerald-600 dark:text-emerald-400',
                activeClass: 'border-emerald-400 shadow-md bg-white/90 dark:bg-slate-900/90',
                inactiveClass: 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-emerald-200/80',
                gradientClass: 'from-emerald-50/70 to-teal-50/70 dark:from-emerald-950/20 dark:to-teal-950/20',
                iconClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300'
            },
            {
                key: 'transporteur' as AdminTab,
                label: 'Transporteur',
                icon: Truck,
                count: tabCounts.transporteur,
                countClass: 'text-orange-600 dark:text-orange-400',
                activeClass: 'border-orange-400 shadow-md bg-white/90 dark:bg-slate-900/90',
                inactiveClass: 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-orange-200/80',
                gradientClass: 'from-orange-50/70 to-amber-50/70 dark:from-orange-950/20 dark:to-amber-950/20',
                iconClass: 'bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300'
            }
        ].filter(Boolean) as Array<{
            key: AdminTab;
            label: string;
            icon: React.ComponentType<{ className?: string }>;
            count: number;
            countClass: string;
            activeClass: string;
            inactiveClass: string;
            gradientClass: string;
            iconClass: string;
        }>;

        return (
            <div className="px-3 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-6">
                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={cn(
                                    'group relative overflow-hidden rounded-xl sm:rounded-2xl border px-2 sm:px-4 py-2 sm:py-3 text-left transition-all duration-200',
                                    isActive ? tab.activeClass : tab.inactiveClass
                                )}
                            >
                                <div
                                    className={cn(
                                        'absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 bg-gradient-to-r',
                                        tab.gradientClass
                                    )}
                                />
                                <div className="relative flex items-center justify-between gap-1 sm:gap-3">
                                    <div className="flex items-center gap-1 sm:gap-3">
                                        <div className={cn('hidden sm:flex h-9 w-9 items-center justify-center rounded-full', tab.iconClass)}>
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className={cn('flex sm:hidden h-5 w-5 items-center justify-center rounded-full', tab.iconClass)}>
                                            <Icon className="h-2.5 w-2.5" />
                                        </div>
                                        <span className={cn(
                                            'text-[8px] sm:text-[11px] font-semibold uppercase leading-tight',
                                            isActive ? 'text-slate-700 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'
                                        )}>
                                            {tab.label}
                                        </span>
                                    </div>
                                    <span className={cn('text-xs sm:text-xl font-bold', tab.countClass)}>{tab.count}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderClientTab = () => (
        <div className="md:h-full md:min-h-0 flex flex-col gap-3 sm:gap-6">
            <ModuleHeader
                icon={Building2}
                title="Clients"
                subtitle="Créez et gérez vos clients et les comptes associés."
                actions={
                    <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap">
                        <div className="relative flex-1 min-w-0 sm:flex-none sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                                id="client-search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Rechercher..."
                                className="pl-10 h-9 sm:h-10 text-sm"
                                autoComplete="off"
                            />
                        </div>
                        <div className="flex items-center justify-center gap-0.5 sm:gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-0.5 sm:p-1">
                            <button
                                type="button"
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 sm:p-2 rounded ${viewMode === 'grid'
                                    ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700'
                                    : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}
                            >
                                <Grid3x3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 sm:p-2 rounded ${viewMode === 'list'
                                    ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700'
                                    : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}
                            >
                                <ListIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('table')}
                                className={`hidden sm:block p-1.5 sm:p-2 rounded ${viewMode === 'table'
                                    ? 'bg-purple-100 dark:bg-purple-950/30 text-purple-700'
                                    : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                    }`}
                            >
                                <TableIcon className="h-4 w-4" />
                            </button>
                        </div>
                        <AddButton
                            label="Nouveau Client"
                            onClick={() => setShowCreateModal(true)}
                            className="w-full sm:w-auto"
                        />
                    </div>
                }
            />

            <div className="md:flex-1 md:min-h-0 md:overflow-auto">
                <div className="pt-2 sm:pt-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
                        </div>
                        // ) : filteredUsers.length === 0 ? (
                        //     <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400">
                        //         <Users className="h-16 w-16 mb-4 opacity-50" />
                        //         <p className="text-lg font-medium">Aucun utilisateur trouvé</p>
                        //     </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-4">
                            {sortedAndPaginatedUsers.map((user) => (
                                <div
                                    key={`${user.user_type}-${user.id}`}
                                    onClick={() => navigate(`/achat/clients/${user.id}?tab=contacts`)}
                                    className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-5 shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-700 transition-all cursor-pointer group"
                                >
                                    <div className="flex items-start justify-between mb-2 sm:mb-4 gap-2">
                                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-purple-100 to-pink-100 text-purple-700 dark:from-purple-900/30 dark:to-pink-900/30 dark:text-purple-400">
                                                <Building2 className="h-4 w-4 sm:h-6 sm:w-6" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="font-semibold text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate">
                                                    {user.company_name || `${user.first_name} ${user.last_name}`}
                                                </h3>
                                                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                                    <span className={cn('px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider', getStatusColor(user.status))}>
                                                        {getStatusLabel(user.status)}
                                                    </span>
                                                    <span className={cn(
                                                        "inline-flex items-center gap-0.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] uppercase font-bold tracking-wide",
                                                        user.is_active
                                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                                    )}>
                                                        {user.is_active ? <CheckCircle className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                                                        {user.is_active ? 'Actif' : 'Inactif'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-2 sm:mb-4">
                                        <div className="flex items-center gap-1.5 sm:gap-2">
                                            <Mail className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400 flex-shrink-0" />
                                            <span className="truncate">{user.email}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 sm:gap-2">
                                            <Phone className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-slate-400 flex-shrink-0" />
                                            <span>{user.phone || '—'}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-end pt-2 sm:pt-3 border-t border-slate-100 dark:border-slate-700">
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleEdit(user); }}
                                                className="p-1.5 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                                title="Modifier"
                                            >
                                                <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleResetPassword(user); }}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                title="Réinitialiser le mot de passe"
                                            >
                                                <Key className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleToggleActive(user); }}
                                                className={cn(
                                                    "p-1.5 rounded-lg transition-colors",
                                                    user.is_active
                                                        ? "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                                        : "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                                                )}
                                                title={user.is_active ? "Désactiver le compte" : "Activer le compte"}
                                            >
                                                <Power className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDelete(user); }}
                                                className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : viewMode === 'list' ? (
                        <div className="space-y-2 sm:space-y-3">
                            {sortedAndPaginatedUsers.map((user) => (
                                <div
                                    key={`${user.user_type}-${user.id}`}
                                    onClick={() => navigate(`/achat/clients/${user.id}?tab=contacts`)}
                                    className="bg-white dark:bg-slate-800 rounded-xl p-3 sm:p-4 shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-700 transition-all flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 cursor-pointer group"
                                >
                                    {/* Header row with avatar + name + badges */}
                                    <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                                        <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-purple-100 to-pink-100 text-purple-700 dark:from-purple-900/30 dark:to-pink-900/30 dark:text-purple-400">
                                            <Building2 className="h-4 w-4 sm:h-6 sm:w-6" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-semibold text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate">
                                                {user.company_name || `${user.first_name} ${user.last_name}`}
                                            </h3>
                                            <div className="flex items-center gap-1 sm:gap-2 flex-wrap mt-0.5">
                                                <span className={cn('px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider', getStatusColor(user.status))}>
                                                    {getStatusLabel(user.status)}
                                                </span>
                                                <span className={cn(
                                                    "inline-flex items-center gap-0.5 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] uppercase font-bold tracking-wide",
                                                    user.is_active
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                                )}>
                                                    {user.is_active ? <CheckCircle className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                                                    {user.is_active ? 'Actif' : 'Inactif'}
                                                </span>
                                            </div>
                                            {/* Desktop info inline */}
                                            <div className="hidden sm:flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                                <div className="flex items-center gap-1.5 min-w-0">
                                                    <Mail className="h-3.5 w-3.5" />
                                                    <span className="truncate">{user.email}</span>
                                                </div>
                                                {user.phone && (
                                                    <div className="flex items-center gap-1.5">
                                                        <Phone className="h-3.5 w-3.5" />
                                                        <span>{user.phone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Mobile info rows */}
                                    <div className="sm:hidden space-y-1 text-xs text-slate-500 dark:text-slate-400 pl-1">
                                        <div className="flex items-center gap-1.5">
                                            <Mail className="h-3 w-3 flex-shrink-0 text-slate-400" />
                                            <span className="truncate">{user.email}</span>
                                        </div>
                                        {user.phone && (
                                            <div className="flex items-center gap-1.5">
                                                <Phone className="h-3 w-3 flex-shrink-0 text-slate-400" />
                                                <span>{user.phone}</span>
                                            </div>
                                        )}
                                    </div>
                                    {/* Actions */}
                                    <div className="flex items-center gap-1 justify-end border-t sm:border-t-0 border-slate-100 dark:border-slate-800 pt-2 sm:pt-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleEdit(user); }}
                                            className="p-1.5 sm:p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                                            title="Modifier"
                                        >
                                            <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleResetPassword(user); }}
                                            className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                                            title="Réinitialiser le mot de passe"
                                        >
                                            <Key className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleToggleActive(user); }}
                                            className={cn(
                                                "p-1.5 sm:p-2 rounded-lg transition-colors",
                                                user.is_active
                                                    ? "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30"
                                                    : "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                                            )}
                                            title={user.is_active ? "Désactiver le compte" : "Activer le compte"}
                                        >
                                            <Power className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDelete(user); }}
                                            className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                            title="Supprimer"
                                        >
                                            <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            {sortedAndPaginatedUsers.length === 0 && (
                                <div className="col-span-full flex flex-col items-center justify-center py-16">
                                    <Users className="h-16 w-16 text-slate-400 mb-4" />
                                    <div className="text-center">
                                        <p className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">Aucun client trouvé</p>
                                        <p className="text-sm text-slate-500 dark:text-slate-500">Essayez de modifier vos critères de recherche</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
                            <div className="overflow-x-auto overflow-y-auto">
                                <Table className="w-full min-w-[800px]">
                                    <TableHeader className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border-b-2 border-blue-200 dark:border-blue-800">
                                        <TableRow>
                                            <TableHead className="min-w-[200px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Nom</span>
                                                    {sortColumn === 'name' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[200px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('email')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Email</span>
                                                    {sortColumn === 'email' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('phone')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Téléphone</span>
                                                    {sortColumn === 'phone' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[100px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('postal_code')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Code Postal</span>
                                                    {sortColumn === 'postal_code' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('city')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Commune</span>
                                                    {sortColumn === 'city' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[120px] px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('client_type')} className="flex items-center gap-1 mx-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Type</span>
                                                    {sortColumn === 'client_type' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[100px] px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('orders')} className="flex items-center gap-1 ml-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Commandes</span>
                                                    {sortColumn === 'orders' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[120px] px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('ca')} className="flex items-center gap-1 ml-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>CA Total</span>
                                                    {sortColumn === 'ca' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[120px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Email vérifié</TableHead>
                                            <TableHead className="min-w-[100px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Statut</span>
                                                    {sortColumn === 'status' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[100px] px-4 py-3 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Compte</TableHead>
                                            <TableHead className="min-w-[120px] px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 mx-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Date Création</span>
                                                    {sortColumn === 'created_at' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead className="min-w-[140px] px-4 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                                                <button onClick={() => handleSort('last_login')} className="flex items-center gap-1 mx-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                                    <span>Dernière Connexion</span>
                                                    {sortColumn === 'last_login' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                                                </button>
                                            </TableHead>
                                            <TableHead
                                                className="sticky right-0 min-w-[120px] px-4 py-3 text-right text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border-l border-blue-200/70 dark:border-blue-800/70 z-10"
                                            >
                                                Actions
                                            </TableHead>
                                        </TableRow>
                                        <TableRow className="bg-slate-100/50 dark:bg-slate-800/50">
                                            <TableHead className="py-2 px-2">
                                                <input
                                                    type="text"
                                                    placeholder="Filtrer..."
                                                    value={columnFilters.name}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, name: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                />
                                            </TableHead>
                                            <TableHead className="py-2 px-2">
                                                <input
                                                    type="text"
                                                    placeholder="Filtrer..."
                                                    value={columnFilters.email}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, email: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                />
                                            </TableHead>
                                            <TableHead className="py-2 px-2">
                                                <input
                                                    type="text"
                                                    placeholder="Filtrer..."
                                                    value={columnFilters.phone}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, phone: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                />
                                            </TableHead>
                                            <TableHead className="py-2 px-2">
                                                <input
                                                    type="text"
                                                    placeholder="Filtrer..."
                                                    value={columnFilters.postal_code}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, postal_code: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                />
                                            </TableHead>
                                            <TableHead className="py-2 px-2">
                                                <input
                                                    type="text"
                                                    placeholder="Filtrer..."
                                                    value={columnFilters.city}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, city: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                />
                                            </TableHead>
                                            <TableHead className="py-2 px-2">
                                                <select
                                                    value={columnFilters.client_type}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, client_type: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="pharmacie">Pharmacie</option>
                                                    <option value="parapharmacie">Parapharmacie</option>
                                                    <option value="centrale">Centrale d'Achat</option>
                                                    <option value="magasin_bio">Magasin Bio</option>
                                                    <option value="therapeute">Thérapeute</option>
                                                    <option value="member">Client Membre</option>
                                                    <option value="independent">Indépendant</option>
                                                </select>
                                            </TableHead>
                                            <TableHead className="py-2 px-2"></TableHead>
                                            <TableHead className="py-2 px-2"></TableHead>
                                            <TableHead className="py-2 px-2">
                                                <select
                                                    value={columnFilters.email_verified}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, email_verified: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="verifie">Vérifié</option>
                                                    <option value="non-verifie">Non vérifié</option>
                                                </select>
                                            </TableHead>
                                            <TableHead className="py-2 px-2">
                                                <select
                                                    value={columnFilters.status}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, status: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="prospect">Prospect</option>
                                                    <option value="actif">Actif</option>
                                                    <option value="inactif">Inactif</option>
                                                    <option value="bloque">Bloqué</option>
                                                </select>
                                            </TableHead>
                                            <TableHead className="py-2 px-2">
                                                <select
                                                    value={columnFilters.is_active}
                                                    onChange={(e) => setColumnFilters({ ...columnFilters, is_active: e.target.value })}
                                                    className="w-full px-2 py-1 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800"
                                                >
                                                    <option value="">Tous</option>
                                                    <option value="actif">Actif</option>
                                                    <option value="inactif">Inactif</option>
                                                </select>
                                            </TableHead>
                                            <TableHead className="py-2 px-2"></TableHead>
                                            <TableHead className="py-2 px-2"></TableHead>
                                            <TableHead className="sticky right-0 py-2 px-2 bg-slate-100/50 dark:bg-slate-800/50 border-l border-blue-200/70 dark:border-blue-800/70"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody className="divide-y divide-slate-200 dark:divide-slate-700">
                                        {sortedAndPaginatedUsers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={14} className="text-center py-8 text-slate-500 dark:text-slate-400">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <Users className="h-8 w-8 opacity-50" />
                                                        <span>Aucun résultat trouvé</span>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : sortedAndPaginatedUsers.map((user) => (
                                            <TableRow
                                                key={`${user.user_type}-${user.id}`}
                                                onClick={() => navigate(`/achat/clients/${user.id}?tab=contacts`)}
                                                className="hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors duration-200 cursor-pointer"
                                            >
                                                <TableCell>{user.company_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—'}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>{user.phone || '—'}</TableCell>
                                                <TableCell className="font-medium">{user.postal_code || '—'}</TableCell>
                                                <TableCell className="truncate max-w-[150px]" title={user.city || ''}>{user.city || '—'}</TableCell>
                                                <TableCell className="text-center">
                                                    <span className={cn(
                                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide",
                                                        getBuyerTypeStyle(user.client_type)
                                                    )}>
                                                        {getBuyerTypeLabel(user.client_type)}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-right font-semibold">{user.orders_count || 0}</TableCell>
                                                <TableCell className="text-right font-bold text-emerald-600 dark:text-emerald-400">{(user.total_ca || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</TableCell>
                                                <TableCell>
                                                    <span className={cn(
                                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide",
                                                        user.is_email_verified
                                                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                    )}>
                                                        {user.is_email_verified ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                                        {user.is_email_verified ? 'Vérifié' : 'Non vérifié'}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={cn(
                                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide",
                                                        getStatusColor(user.status)
                                                    )}>
                                                        {getStatusLabel(user.status)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={cn(
                                                        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide",
                                                        user.is_active
                                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                                    )}>
                                                        {user.is_active ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                                        {user.is_active ? 'Actif' : 'Inactif'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-center text-sm text-slate-600 dark:text-slate-400">
                                                    {new Date(user.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </TableCell>
                                                <TableCell className="text-center text-sm text-slate-600 dark:text-slate-400">
                                                    {user.last_login
                                                        ? new Date(user.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(user.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                                                        : <span className="italic text-slate-400">Jamais connecté</span>
                                                    }
                                                </TableCell>
                                                <TableCell className="sticky right-0 min-w-[160px] bg-white dark:bg-slate-900 border-l border-blue-200/70 dark:border-blue-800/70 z-10">
                                                    <div className="flex items-center justify-end gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(user);
                                                            }}
                                                            className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950/30 rounded-lg transition-colors"
                                                            title="Modifier"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleResetPassword(user);
                                                            }}
                                                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                                                            title="Réinitialiser le mot de passe"
                                                        >
                                                            <Key className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleActive(user);
                                                            }}
                                                            className={cn(
                                                                "p-2 rounded-lg transition-colors",
                                                                user.is_active
                                                                    ? "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                                                                    : "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                                                            )}
                                                            title={user.is_active ? "Désactiver le compte" : "Activer le compte"}
                                                        >
                                                            <Power className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(user);
                                                            }}
                                                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                                                            title="Supprimer"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {filteredUsers.length > pageSize && (
                                <div className="flex items-center justify-between px-4 py-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm mt-4">
                                    <div className="text-sm text-slate-500 dark:text-slate-400">
                                        Affichage de <span className="font-semibold text-slate-700 dark:text-slate-200">{(page - 1) * pageSize + 1}</span> à <span className="font-semibold text-slate-700 dark:text-slate-200">{Math.min(page * pageSize, filteredUsers.length)}</span> sur <span className="font-semibold text-slate-700 dark:text-slate-200">{filteredUsers.length}</span> clients
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Précédent
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.ceil(filteredUsers.length / pageSize) }, (_, i) => i + 1)
                                                .filter(pageNum => {
                                                    const totalPages = Math.ceil(filteredUsers.length / pageSize);
                                                    if (totalPages <= 7) return true;
                                                    return pageNum === 1 ||
                                                        pageNum === totalPages ||
                                                        (pageNum >= page - 2 && pageNum <= page + 2);
                                                })
                                                .map((pageNum, i, arr) => {
                                                    const showEllipsisBefore = i > 0 && pageNum - arr[i - 1] > 1;
                                                    return (
                                                        <React.Fragment key={pageNum}>
                                                            {showEllipsisBefore && <span className="px-2 text-slate-400">...</span>}
                                                            <button
                                                                onClick={() => setPage(pageNum)}
                                                                className={`min-w-[2.5rem] px-3 py-2 text-sm font-medium rounded-lg transition-colors ${page === pageNum
                                                                    ? 'bg-purple-600 text-white'
                                                                    : 'text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                                                                    }`}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        </React.Fragment>
                                                    );
                                                })}
                                        </div>
                                        <button
                                            onClick={() => setPage(p => Math.min(Math.ceil(filteredUsers.length / pageSize), p + 1))}
                                            disabled={page >= Math.ceil(filteredUsers.length / pageSize)}
                                            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            Suivant
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderPlaceholder = (title: string, tab: AdminTab) => (
        <div className="flex-1 flex flex-col items-center justify-center p-20 text-center">
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6">
                {tab === 'fournisseur' ? <Package className="h-10 w-10 text-slate-400" /> : <Truck className="h-10 w-10 text-slate-400" />}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">{title}</h2>
            <p className="text-slate-500 max-w-md mb-8">Cette section est en cours de développement. Le contenu sera disponible prochainement.</p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Nouveau {tab === 'fournisseur' ? 'Fournisseur' : 'Transporteur'}
            </Button>
        </div>
    );

    return (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-50 via-purple-50/20 to-slate-50 dark:from-slate-950 dark:via-purple-950/10 dark:to-slate-950 overflow-y-auto md:overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 via-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Shield className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Administration</h1>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Gestion globale de la plateforme</p>
                    </div>
                </div>
            </div>

            {renderTabs()}

            <div className="md:flex-1 flex flex-col min-h-0 md:overflow-hidden px-3 sm:px-6 pb-3 sm:pb-6">
                {activeTab === 'admin' && (
                    <div className="md:flex-1 md:overflow-auto">
                        <SuperAdminAdmins defaultRoleFilter="admin" />
                    </div>
                )}
                {activeTab === 'apporteur' && (
                    <div className="md:flex-1 md:overflow-auto">
                        <SuperAdminAdmins defaultRoleFilter="apporteur" />
                    </div>
                )}
                {activeTab === 'client' && renderClientTab()}
                {activeTab === 'fournisseur' && renderPlaceholder('Gestion des Fournisseurs', 'fournisseur')}
                {activeTab === 'transporteur' && renderPlaceholder('Gestion des Transporteurs', 'transporteur')}
            </div>

            <ClientCreateModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={onClientCreated}
            />

            <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Modifier le client</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2 sm:col-span-2">
                                <label className="text-sm font-medium">Nom / Raison sociale</label>
                                <Input
                                    value={editForm.company_name || editForm.last_name || ''}
                                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value, last_name: e.target.value })}
                                    placeholder="Nom du client"
                                />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <label className="text-sm font-medium">Email</label>
                                <Input
                                    type="email"
                                    value={editForm.email || ''}
                                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                    placeholder="email@exemple.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Statut</label>
                                <select
                                    value={editForm.status || 'prospect'}
                                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as any })}
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
                                >
                                    <option value="prospect">Prospect</option>
                                    <option value="actif">Actif</option>
                                    <option value="inactif">Inactif</option>
                                    <option value="bloque">Bloqué</option>
                                    <option value="ferme">Fermé</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Compte</label>
                                <select
                                    value={editForm.is_active ? 'active' : 'inactive'}
                                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'active' })}
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm"
                                >
                                    <option value="active">Actif</option>
                                    <option value="inactive">Inactif</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button variant="outline" onClick={() => setEditModal(null)}>Annuler</Button>
                        <Button onClick={handleSaveEdit}>Enregistrer</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Administration;
